import axios from 'axios';
import React, { useState, useEffect, useRef } from 'react';
import { apiClient, showToast } from '../../utils/apiClient';
import { API } from '../../config';
import { formatDate, formatDateTime, formatNotesWithBullets, getDayOfWeek, formatDateWithDay, getLocationDisplay, getLocationChipColor, getRowAlert, formatDatetimeForInput, parseUTCDatetime, getTodayInAEST, isPrepaymentApproaching, hasAppointments, getAppointmentSummary } from '../../utils/helpers';
import { useAuth } from '../../context/AuthContext';


const PendingTasksSection = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddTask, setShowAddTask] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [newTask, setNewTask] = useState({
    client_name: '',
    pet_name: '',
    reason: '',
    payment_status: ''
  });

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await apiClient.get('/pending-tasks');
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = async () => {
    try {
      if (!newTask.client_name || !newTask.pet_name || !newTask.reason) {
        showToast('Please fill in all required fields', 'error');
        return;
      }

      await apiClient.post('/pending-tasks', {
        ...newTask,
        created_by: user?.id
      });

      showToast('Task added successfully! ✅');
      setShowAddTask(false);
      setNewTask({ client_name: '', pet_name: '', reason: '', payment_status: '' });
      fetchTasks();
    } catch (error) {
      console.error('Failed to add task:', error);
      showToast('Failed to add task', 'error');
    }
  };

  const handleEditTask = (task) => {
    setEditingTaskId(task.id);
    setNewTask({
      client_name: task.client_name,
      pet_name: task.pet_name,
      reason: task.reason,
      payment_status: task.payment_status
    });
    setShowAddTask(false); // Close add form if open
  };

  const handleUpdateTask = async (taskId) => {
    try {
      if (!newTask.client_name || !newTask.pet_name || !newTask.reason) {
        showToast('Please fill in all required fields', 'error');
        return;
      }

      await apiClient.put(`/pending-tasks/${taskId}`, newTask);
      showToast('Task updated successfully! ✅');
      setEditingTaskId(null);
      setNewTask({ client_name: '', pet_name: '', reason: '', payment_status: '' });
      fetchTasks();
    } catch (error) {
      console.error('Failed to update task:', error);
      showToast('Failed to update task', 'error');
    }
  };

  const handleCancelEdit = () => {
    setEditingTaskId(null);
    setNewTask({ client_name: '', pet_name: '', reason: '', payment_status: '' });
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await apiClient.put(`/pending-tasks/${taskId}/complete`);
      showToast('Task marked as complete ✅');
      fetchTasks();
    } catch (error) {
      console.error('Failed to complete task:', error);
      showToast('Failed to complete task', 'error');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await apiClient.delete(`/pending-tasks/${taskId}`);
      showToast('Task deleted');
      fetchTasks();
    } catch (error) {
      console.error('Failed to delete task:', error);
      showToast('Failed to delete task', 'error');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-base font-bold text-gray-900">📞 Awaiting Calls & Pending Tasks</h3>
        <button
          onClick={() => setShowAddTask(!showAddTask)}
          className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 rounded-lg font-medium text-xs flex items-center space-x-1"
        >
          <span className="text-sm">+</span>
          <span>Add Task</span>
        </button>
      </div>

      {/* Add Task Form */}
      {showAddTask && (
        <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <input
              type="text"
              placeholder="Client Name *"
              value={newTask.client_name}
              onChange={(e) => setNewTask({...newTask, client_name: e.target.value})}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
            />
            <input
              type="text"
              placeholder="Pet Name *"
              value={newTask.pet_name}
              onChange={(e) => setNewTask({...newTask, pet_name: e.target.value})}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
            />
          </div>
          <textarea
            placeholder="Reason / What are you waiting for? *"
            value={newTask.reason}
            onChange={(e) => setNewTask({...newTask, reason: e.target.value})}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm mb-3"
          />
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Payment Status (e.g., Pending $450, Paid, N/A)"
              value={newTask.payment_status}
              onChange={(e) => setNewTask({...newTask, payment_status: e.target.value})}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
            />
            <button
              onClick={handleAddTask}
              className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-medium text-sm"
            >
              Save
            </button>
            <button
              onClick={() => setShowAddTask(false)}
              className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg font-medium text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Tasks Table */}
      {loading ? (
        <div className="text-center py-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
        </div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-6 text-gray-500">
          <p className="text-sm">No pending tasks</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="px-2 py-1.5 text-left font-semibold text-gray-700">CLIENT</th>
                <th className="px-2 py-1.5 text-left font-semibold text-gray-700">PET</th>
                <th className="px-2 py-1.5 text-left font-semibold text-gray-700">REASON</th>
                <th className="px-2 py-1.5 text-left font-semibold text-gray-700">PAYMENT</th>
                <th className="px-2 py-1.5 text-center font-semibold text-gray-700">ACTIONS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {tasks.map((task) => (
                editingTaskId === task.id ? (
                  // Edit Mode Row
                  <tr key={task.id} className="bg-blue-50">
                    <td className="px-2 py-2">
                      <input
                        type="text"
                        value={newTask.client_name}
                        onChange={(e) => setNewTask({...newTask, client_name: e.target.value})}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="text"
                        value={newTask.pet_name}
                        onChange={(e) => setNewTask({...newTask, pet_name: e.target.value})}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="text"
                        value={newTask.reason}
                        onChange={(e) => setNewTask({...newTask, reason: e.target.value})}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="text"
                        value={newTask.payment_status}
                        onChange={(e) => setNewTask({...newTask, payment_status: e.target.value})}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                      />
                    </td>
                    <td className="px-2 py-2 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => handleUpdateTask(task.id)}
                          className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                          title="Save changes"
                        >
                          ✓
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="text-gray-600 hover:text-gray-800 font-medium text-sm"
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  // View Mode Row
                  <tr key={task.id} className="hover:bg-gray-50">
                    <td className="px-2 py-2 font-medium text-gray-900">{task.client_name}</td>
                    <td className="px-2 py-2 text-gray-700">{task.pet_name}</td>
                    <td className="px-2 py-2 text-gray-700">{task.reason}</td>
                    <td className="px-2 py-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        task.payment_status.toLowerCase().includes('paid') 
                          ? 'bg-green-100 text-green-800'
                          : task.payment_status.toLowerCase().includes('pending')
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {task.payment_status}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => handleEditTask(task)}
                          className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                          title="Edit task"
                        >
                          ✎
                        </button>
                        <button
                          onClick={() => handleCompleteTask(task.id)}
                          className="text-green-600 hover:text-green-800 font-medium text-sm"
                          title="Mark as complete"
                        >
                          ✓
                        </button>
                        <button
                          onClick={() => handleDeleteTask(task.id)}
                          className="text-red-600 hover:text-red-800 font-medium text-sm"
                          title="Delete task"
                        >
                          ✕
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Calendar View Component

export default PendingTasksSection;
